import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import CrmSyncReview from "./CrmSyncReview";
import { getMyInfoData } from "../../utils/myinfoData";
import singpassLogo from "../../assets/singpass/singpass-full-colour.png";

const API = "https://www.smart-lend.com/api/property-co-owner-myinfo";
const sessionKey = (state) => `property-co-owner-myinfo:${state}`;
const formatAddress = (address) =>
  [
    address?.block?.value,
    address?.street?.value,
    address?.floor?.value && address?.unit?.value
      ? `#${address.floor.value}-${address.unit.value}`
      : "",
    address?.postal?.value,
    address?.country?.desc,
  ]
    .filter(Boolean)
    .join(", ");

const post = async (action, values) => {
  const response = await fetch(`${API}/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(values),
  });
  const data = await response.json();
  if (!response.ok)
    throw new Error(
      data.message || "Unable to continue. Please contact support.",
    );
  return data;
};

export default function PropertyCoOwnerMyinfo() {
  const history = useHistory();
  const [context] = useState(() => {
    const query = new URLSearchParams(window.location.search);
    const state = query.get("state");
    return {
      state,
      code: query.get("code"),
      error: query.get("error"),
      token:
        query.get("invitation_token") ||
        query.get("token") ||
        (state && sessionStorage.getItem(sessionKey(state))),
    };
  });
  const [invitation, setInvitation] = useState(null);
  const [person, setPerson] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const [completed, setCompleted] = useState(false);
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (context.error)
          throw new Error(
            "Myinfo sharing was cancelled. Reopen your invitation link to try again.",
          );
        if (!context.token)
          throw new Error(
            context.state
              ? "This Myinfo session is incomplete. Reopen the invitation link in the same browser to try again."
              : "This page needs your complete invitation link. Please open the full link provided by SmartLend support, or ask them for a new one.",
          );
        let data;
        if (context.state) {
          data = await post("resolve", {
            token: context.token,
            state: context.state,
            ...(context.code ? { code: context.code } : {}),
          });
          if (active) {
            setPerson(getMyInfoData(data.myinfo_data));
            setCompleted(Boolean(data.completed));
            history.replace(
              `/property-co-owner-myinfo?state=${encodeURIComponent(context.state)}`,
            );
          }
        } else {
          const response = await fetch(
            `${API}/invitation?token=${encodeURIComponent(context.token)}`,
            { headers: { Accept: "application/json" } },
          );
          data = await response.json();
          if (!response.ok)
            throw new Error(
              data.message ||
                "This invitation is unavailable. Ask support for a new link.",
            );
          if (active) setCompleted(data.status === "completed");
        }
        if (active) setInvitation(data);
      } catch (err) {
        if (active) {
          setError(err.message);
          if (context.state)
            history.replace(
              `/property-co-owner-myinfo?state=${encodeURIComponent(context.state)}`,
            );
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [context, history]); // post only uses the supplied payload and constant API URL.
  const start = async () => {
    if (!consent || busy) return;
    setBusy(true);
    setError("");
    try {
      const data = await post("start", { token: context.token, consent: true });
      sessionStorage.setItem(sessionKey(data.state), context.token);
      window.location.assign(data.singpass_url);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await post("confirm", {
        token: context.token,
        state: context.state,
        consent: true,
      });
      sessionStorage.removeItem(sessionKey(context.state));
      setCompleted(true);
      setPerson(null);
      history.replace("/property-co-owner-myinfo");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  if (person && !completed)
    return (
      <>
        {error && (
          <p role="alert" className="crm-review-status">
            {error}
          </p>
        )}
        <CrmSyncReview
          flow="property-co-owner"
          invitedName={invitation?.name}
          dataSource="myinfo"
          personData={person}
          busy={busy}
          onConfirm={confirm}
          formatAddress={formatAddress}
          formatDate={(value) => value || "—"}
          formatYearMonth={(value) => value || "—"}
        />
      </>
    );
  return (
    <div className="crm-review-page">
      <header className="crm-review-header">
        <a className="crm-review-brand" href="/home">
          SmartLend
        </a>
      </header>
      <main className="crm-review-main">
        <section className="crm-review-status">
          <h1>
            {completed
              ? "Myinfo details saved"
              : "Optional co-owner Myinfo sharing"}
          </h1>
          {loading ? (
            <p role="status">Loading your invitation…</p>
          ) : completed ? (
            <p>
              Your Myinfo details have been saved for SmartLend support to review.
              They will be visible to lenders only after support approves them.
              You can close this page.
            </p>
          ) : (
            <>
              {error && <p role="alert">{error}</p>}
              {invitation && (
                <>
                  <p>
                    This invitation is for <strong>{invitation.name}</strong>.
                    Please use your own Singpass account.
                  </p>
                  <p>
                    Your details will be attached to this property loan case and
                    available to SmartLend and the main applicant. Lenders
                    reviewing the case can see them only after support approves
                    the scan.
                  </p>
                  <label
                    className="property-co-owner-consent"
                    style={{
                      display: "flex",
                      gap: 10,
                      textAlign: "left",
                      margin: "20px 0",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={consent}
                      disabled={busy}
                      onChange={(event) => setConsent(event.target.checked)}
                    />
                    I agree to retrieve and share my Myinfo for this property
                    loan case.
                  </label>
                  <button
                    type="button"
                    className="property-co-owner-start"
                    onClick={start}
                    disabled={!consent || busy}
                    style={{
                      padding: 12,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <img
                      src={singpassLogo}
                      alt="Singpass"
                      width="96"
                      height="28"
                    />
                    {busy ? "Connecting…" : "Continue with Myinfo"}
                  </button>
                </>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}
